import { useState } from 'react';
import { useFormikContext } from 'formik';

import useToast from '../../hooks/useToast';
import {
  getApproversOyM,
  getInspectors,
  getInspectorsEyM,
  postApproversContracts
} from '../../services/adminContracts';
import { postApproversServices } from '../../services/yclickMaster';
import { ubicationTypeEnum } from '../../utils/dataStatus';

const useAssociatedUsersForm = (props) => {
  const {
    requestValue,
    serviceId,
    ubicationTypeId,
    ubicationTeamValue = [],
    imptationObjectWells,
    associatedUsersValue,
    associatedUsersField,
    contracts,
    isOyM,
    idEvento = null
  } = props;

  const [approverOptions, setApproverOptions] = useState([]);
  const [inspectorsOptions, setInspectorsOptions] = useState([]);

  const { setFieldValue, values } = useFormikContext();
  const { setNewToast } = useToast();

  const getDataApproverService = async () => {
    try {
      const hasUbicationTeam = Array.isArray(ubicationTeamValue)
        ? ubicationTeamValue
        : [ubicationTeamValue];

      const imputationObjectHasMultipleWells = imptationObjectWells?.pozos ?? imptationObjectWells;
      const listaEquipoIds = [
        ...new Set(
          ubicationTypeId === ubicationTypeEnum.pozo
            ? hasUbicationTeam
            : imputationObjectHasMultipleWells.map((item) => item.team?.value || item.equipo?.id)
        )
      ];

      const bodyRequest = {
        servicioId: serviceId,
        listaEquipoIds
      };

      return postApproversServices(bodyRequest);
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
      throw error;
    }
  };

  const getEventId = (val) => {
    const ubication = val?.ubication;
    const ubicationTypeIdLocation = ubication?.ubicationType?.id;
    const isWell = ubicationTypeEnum.pozo === ubicationTypeIdLocation;
    const isLocation = ubicationTypeEnum.locacion === ubicationTypeIdLocation;

    if (idEvento) {
      return idEvento;
    }

    if (isLocation) {
      return ubication?.typeEvent?.id;
    }

    return isWell && ubication?.event?.id === 0
      ? ubication?.operationType?.id
      : ubication?.event?.id;
  };

  const getDataApproverByProvider = async (providers) => {
    try {
      const body = providers.length
        ? providers?.map((item) => {
            if (item.idUnidadNegocio && item.nroContrato && idEvento) return { ...item, idEvento };
            if (!item.contract || !item.businessUnit) {
              // para validar que luego no se llame al servicio
              return null;
            }

            return {
              nroContrato: item?.contract?.nombre.split(' ')[0],
              idUnidadNegocio: item?.businessUnit?.id,
              idEvento: getEventId(values)
            };
          })
        : [];

      const isSomeNull = body.some((b) => !b);

      if (!isSomeNull && body.length) {
        const approversResponse = await postApproversContracts(body);

        return approversResponse.data.aprobadores;
      }

      return [];
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
      throw error;
    }
  };

  const filterMultipleApprovers = (approvers) => {
    // Se inicializa el Map en su constructor iterando a todos los aprobadores.
    // En el caso de que haya aprobadores repetidos se van a pisar por tener la
    // misma key `id` y se lo adapta para poder ser mostrado en el select
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map
    const approversMap = new Map(
      approvers.map((approver) => [
        approver.id,
        {
          esGrupo: approver.esGrupo,
          id: approver.id,
          label: approver.nombre,
          nombre: approver.nombre,
          value: approver.id
        }
      ])
    );

    // Se transforma `approversMap` de nuevo a un array
    return Array.from(approversMap, ([_, value]) => value);
  };

  const getOptionApprover = async () => {
    let dataOptions = [];

    try {
      if (values.isOyM || isOyM) {
        const approverBodyRequest =
          contracts ||
          requestValue?.provider?.providers.map(({ contract }) => ({
            nroContrato: contract.nombre,
            idOperacion: values.ubication.uunn.id
          }));

        dataOptions = await getApproversOyM(approverBodyRequest);
      } else {
        if (requestValue?.provider?.providers) {
          const dataApprover = await getDataApproverByProvider(requestValue?.provider?.providers);

          dataOptions = [...dataApprover];
        } else if (contracts.length) {
          const dataApprover = await getDataApproverByProvider(contracts);

          dataOptions = [...dataApprover];
        }

        if (serviceId && ubicationTypeId && (ubicationTeamValue || imptationObjectWells)) {
          const postSendApproverServices = await getDataApproverService();

          dataOptions = [...dataOptions, ...postSendApproverServices];
        }
      }

      const adapterOptionAppr = filterMultipleApprovers(dataOptions);

      if (!associatedUsersValue?.approvers?.length) {
        setFieldValue(`${associatedUsersField}.approvers`, adapterOptionAppr);
      } else {
        const newApproverValues = associatedUsersValue?.approvers?.filter((appr) =>
          adapterOptionAppr.some((optionAppr) => optionAppr.id === appr.id)
        );

        setFieldValue(`${associatedUsersField}.approvers`, newApproverValues);
      }
      setApproverOptions(adapterOptionAppr);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const getInspectorsOptions = async () => {
    const inspectorBodyRequest = requestValue.provider.providers.map((provider) => ({
      nroContrato: provider.contract.nombre.split(' ')[0],
      [values.isOyM ? 'operacionId' : 'unidadNegocioId']: values.ubication.uunn.id
    }));

    try {
      const inspectorsList = values.isOyM
        ? await getInspectors(inspectorBodyRequest)
        : await getInspectorsEyM(inspectorBodyRequest);

      setInspectorsOptions(inspectorsList);

      if (!associatedUsersValue?.inspectors?.length) {
        setFieldValue(`${associatedUsersField}.inspectors`, inspectorsList);

        return;
      }
      setFieldValue(`${associatedUsersField}.inspectors`, associatedUsersValue?.inspectors);
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    }
  };

  return {
    approverOptions,
    filterMultipleApprovers,
    getDataApproverService,
    getOptionApprover,
    inspectorsOptions,
    getInspectorsOptions
  };
};

export default useAssociatedUsersForm;
